import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, Phone, Mail } from 'lucide-react';
import logo from '../../assets/logo.jpg';
import { navigation, siteInfo } from '../../data/schoolInfo';

const Navbar = ({ division }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const navItems = navigation.main;

  return (
    <>
      {/* Top Bar */}
      <div className="hidden lg:block bg-primary-600 text-white py-2">
        <div className="container-custom flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <a href={`tel:${siteInfo.contact.phone}`} className="flex items-center gap-2 hover:text-primary-200 transition-colors">
              <Phone size={14} />
              {siteInfo.contact.phone}
            </a>
            <a href={`mailto:${siteInfo.contact.email}`} className="flex items-center gap-2 hover:text-primary-200 transition-colors">
              <Mail size={14} />
              {siteInfo.contact.email}
            </a>
          </div>
          <div className="flex items-center gap-4">
            <a href={siteInfo.social.facebook} target="_blank" rel="noopener noreferrer" className="hover:text-primary-200 transition-colors">Facebook</a>
            <a href={siteInfo.social.instagram} target="_blank" rel="noopener noreferrer" className="hover:text-primary-200 transition-colors">Instagram</a>
            <a href={siteInfo.social.twitter} target="_blank" rel="noopener noreferrer" className="hover:text-primary-200 transition-colors">Twitter</a>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white shadow-md py-2'
            : 'bg-white/95 backdrop-blur-sm py-4'
        }`}
      >
        <nav className="container-custom">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <img src={logo} alt="Stirret Schools" className="h-12 md:h-14 w-auto" />
              <div className="hidden sm:block">
                <span className="block text-lg font-bold text-primary-600">Stirret Schools</span>
                <span className="block text-xs text-gray-500">Raising Exceptional Leaders</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => item.children && setActiveDropdown(item.label)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <Link
                    to={item.href}
                    className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      location.pathname === item.href
                        ? 'text-primary-600 bg-primary-50'
                        : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                    }`}
                  >
                    {item.label}
                    {item.children && <ChevronDown size={16} className={`transition-transform ${activeDropdown === item.label ? 'rotate-180' : ''}`} />}
                  </Link>

                  {/* Dropdown Menu */}
                  {item.children && (
                    <AnimatePresence>
                      {activeDropdown === item.label && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-0 mt-2 w-[500px] bg-white rounded-xl shadow-xl border border-gray-100 p-6 grid grid-cols-2 gap-6"
                        >
                          {item.children.map((school) => (
                            <div key={school.title}>
                              <Link to={school.href} className="block group">
                                <h3 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                                  {school.title}
                                </h3>
                                <p className="text-sm text-gray-500">{school.subtitle}</p>
                              </Link>
                              <ul className="mt-3 space-y-2">
                                {school.links.map((link) => (
                                  <li key={link.href}>
                                    <Link
                                      to={link.href}
                                      className="text-sm text-gray-600 hover:text-primary-600 transition-colors"
                                    >
                                      {link.label}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-4">
              <Link to="/admissions" className="btn-primary">
                Apply Now
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 bottom-0 w-full max-w-sm bg-white z-50 overflow-y-auto lg:hidden"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-8">
                  <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
                    <img src={logo} alt="Stirret Schools" className="h-10" />
                  </Link>
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 rounded-lg hover:bg-gray-100"
                  >
                    <X size={24} />
                  </button>
                </div>

                <nav className="space-y-2">
                  {navItems.map((item) => (
                    <div key={item.label}>
                      {item.children ? (
                        <div>
                          <button
                            onClick={() => setActiveDropdown(activeDropdown === item.label ? null : item.label)}
                            className="w-full flex items-center justify-between py-3 text-gray-900 font-medium"
                          >
                            {item.label}
                            <ChevronDown
                              size={20}
                              className={`transition-transform ${activeDropdown === item.label ? 'rotate-180' : ''}`}
                            />
                          </button>
                          <AnimatePresence>
                            {activeDropdown === item.label && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                              >
                                <div className="pl-4 pb-4 space-y-4">
                                  {item.children.map((school) => (
                                    <div key={school.title}>
                                      <Link
                                        to={school.href}
                                        className="block font-medium text-gray-900 mb-2"
                                        onClick={() => setIsMobileMenuOpen(false)}
                                      >
                                        {school.title}
                                      </Link>
                                      <ul className="space-y-2">
                                        {school.links.map((link) => (
                                          <li key={link.href}>
                                            <Link
                                              to={link.href}
                                              className="text-sm text-gray-600"
                                              onClick={() => setIsMobileMenuOpen(false)}
                                            >
                                              {link.label}
                                            </Link>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ) : (
                        <Link
                          to={item.href}
                          className="block py-3 text-gray-900 font-medium"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {item.label}
                        </Link>
                      )}
                    </div>
                  ))}
                </nav>

                <div className="mt-8 pt-8 border-t space-y-4">
                  <Link
                    to="/admissions"
                    className="btn-primary w-full text-center"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Apply Now
                  </Link>
                  <Link
                    to="/contact"
                    className="btn-secondary w-full text-center"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Contact Us
                  </Link>
                </div>

                <div className="mt-8 pt-8 border-t text-sm text-gray-600 space-y-3">
                  <a href={`tel:${siteInfo.contact.phone}`} className="flex items-center gap-2">
                    <Phone size={16} />
                    {siteInfo.contact.phone}
                  </a>
                  <a href={`mailto:${siteInfo.contact.email}`} className="flex items-center gap-2">
                    <Mail size={16} />
                    {siteInfo.contact.email}
                  </a>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
